import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import { useKnowledge } from '../hooks/useKnowledge';
import { usePageMeta } from '../hooks/usePageMeta';

const FILTERS = [
  { key: 'all', label: 'All Areas' },
  { key: 'physical', label: 'Body & Physical' },
  { key: 'mind', label: 'Brain & Mind' },
  { key: 'rest', label: 'Lifestyle & Rest' },
  { key: 'purpose', label: 'Social & Purpose' },
];

// Display-only grouping of the real canonical domains for the filter bar.
const CAT_BY_CODE = {
  D01: 'physical', D04: 'physical', D08: 'physical',
  D02: 'mind',
  D03: 'rest', D05: 'rest', D07: 'rest',
  D06: 'purpose', D09: 'purpose', D10: 'purpose', D11: 'purpose',
};

const MATRIX = [
  { n: '01', title: 'Sleep → Metabolism', desc: 'A single night of sub-6hr sleep induces acute peripheral insulin resistance comparable to metabolic syndrome, elevating next-day ghrelin and blunt dopamine sensitivity.', affect: 'Affects Nutrition & Hydration', icon: 'bolt' },
  { n: '02', title: 'Movement → Neuroplasticity', desc: 'Sustained aerobic exertion in Zone 2 accelerates circulating BDNF (Brain-Derived Neurotrophic Factor), remodeling the hippocampus and optimizing mental clarity.', affect: 'Affects Mental & Cognitive Health', icon: 'psychology' },
  { n: '03', title: 'Stress → Immune System', desc: 'Chronic sympathetic hyperarousal depletes secretor sIgA mucosal defenses and prompts systemic NF-kB signaling, increasing vulnerability to opportunistic infections.', affect: 'Affects Preventive & Healthcare', icon: 'shield' },
];

const GUIDE_IMAGES = [
  'https://lh3.googleusercontent.com/aida-public/AB6AXuB8G7O9jdpiR8_lX973hcevWDaaDlpvfFDJJgT9meVpX6cBb30g2GJUZ3hqsriALtq1nFnz3b2rykfu-x5EJlbpMEetpAWTWVbnjBPwCr19Ev09f2E6uCZP95-N93R3zvqpGnTLp2NyRmaf5qfirkMrlv3nKuEmwLs16vZAqeu66kCVwzFV7Bowd6wW3LXGLACF2muVWHPnHcFRIkgWy9Tzg98WQNbyzxKvtBc_ztROFduIZeN7dQg',
  'https://lh3.googleusercontent.com/aida-public/AB6AXuCxHrCsuvav8odUeLj-oBe7FRK2336HvJ9YgetAn1-QpFZIcwqRHArA5G9X9DU-mzlbuZMaOaD_2koHuKubqCCU6SDlqhFTTF1BvzcGFL_mK_vwqJdUReKLOvBXIcA701t1yI5M2kg1uamToZB3MBTJjhmJP5__X9bU0jMZeoSVyoeQY1UmMhUrVh4sJKT9S5TdFXDPjAm92sDhnrlD7U2xtLHZ3kIa0zo9F70N1Lc86C17iW6seM0',
  'https://lh3.googleusercontent.com/aida-public/AB6AXuC42nf_V-FsHOcWzwzesugjOVbgTLB29FyAs6tOAPCPynWGwrl-wp5jyrmyIvrgNBTtgyCR_HtSTkOzAWTMHFHy5kSxB9wnXL9fOiuQSpQsppN0aTvVmlErfnmYGKeEKE9d7zk6EOzVlFle_qd_LAEA3ufa9FQmVX5Pj87u7YoZ6W9lQv24ZdOYXuk1gg65lAqD2l3CEUYMhTR1XWnaQ7-9tXE5c4Qfo-yy5IGmEmFDuB5Abu73YwE',
  'https://lh3.googleusercontent.com/aida-public/AB6AXuAJZE4sUoLm9Z1hmclVMVqzVz6Gaq2KmYUU9b-qKL99CzxMgNul2rQKadFS0hnstiSMJab7F_U4HXWG4Pnp_EK_o56wi5-r93WRLqk9WJlN2dHF7Vb8r-7E-Ar3BkndhAZXjyvqLbiRtZxmoAy1fsGuNM0x1jcLxjdrvsiEPhrJL4DVwjG_b6BOa0RMnBcMKMmGO2VhFq4h0gZgUzyt9Ev0aoXbsfyF7S_aflVEJV9wc0GDTRf-CeY',
];

export default function Wellbeing() {
  usePageMeta({
    title: 'Wellbeing: Explore Areas of Life | OVERALL WELLBEING',
    description:
      'Every part of daily life, explored step by step — sleep, movement, mind, nutrition, emotions, relationships, environment and more, with practical guidance for each area.',
    path: '/wellbeing',
    image: '/og-wellbeing.jpg',
  });

  const { domains, topics, loading } = useKnowledge();
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const topicsByDomain = useMemo(() => {
    const m = {};
    topics.forEach((t) => { (m[t.domain_code] = m[t.domain_code] || []).push(t); });
    return m;
  }, [topics]);

  const domainCards = useMemo(() => domains.map((d) => {
    const dts = topicsByDomain[d.code] || [];
    return {
      code: d.code,
      slug: d.slug,
      n: String(d.order).padStart(2, '0'),
      cat: CAT_BY_CODE[d.code] || 'physical',
      count: d.topic_count != null ? d.topic_count : dts.length,
      title: d.name,
      description: d.description || '',
      tags: dts.slice(0, 4).map((t) => ({ title: t.title, slug: t.slug })),
      allTopicNames: dts.map((t) => t.title).join(' '),
    };
  }), [domains, topicsByDomain]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return domainCards.filter((d) => {
      const matchCat = filter === 'all' || d.cat === filter;
      const matchQ = !q || `${d.title} ${d.allTopicNames}`.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [domainCards, filter, query]);

  const featuredGuides = useMemo(() => {
    // One representative canonical topic from the first four domains.
    return domains.slice(0, 4).map((d, i) => {
      const t = (topicsByDomain[d.code] || [])[0];
      return t ? { code: d.code, domain: d.name, title: t.title, slug: t.slug, img: GUIDE_IMAGES[i % GUIDE_IMAGES.length] } : null;
    }).filter(Boolean);
  }, [domains, topicsByDomain]);

  return (
    <div className="flex flex-col w-full">
      {/* HERO + SEARCH */}
      <section className="w-full px-margin-mobile lg:px-margin pt-space-2xl pb-space-xl relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-surface-tint/5 blur-3xl pointer-events-none -z-10" />
        <div className="flex flex-col max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-space-sm mb-space-md">
            <span className="font-label-sm text-label-sm tracking-wider uppercase text-on-surface-variant">AREAS OF LIFE</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-end mb-space-2xl">
            <div className="lg:col-span-8">
              <h1 className="font-display-hero text-[40px] leading-[42px] md:text-[56px] md:leading-[58px] lg:text-display-hero uppercase tracking-tight text-outline">
                THE AREAS <br />
                <span className="text-surface-tint drop-shadow-[0_0_24px_rgba(240,40,223,0.3)]">OF LIFE.</span>
              </h1>
            </div>
            <div className="lg:col-span-4 pb-2">
              <p className="font-body-lg text-body-lg text-on-surface-variant leading-relaxed">
                Every part of daily life, explored step by step — with clear, practical guides and simple everyday routines.
              </p>
            </div>
          </div>
          <div className="w-full bg-surface-container-low p-space-md rounded-lg shadow-sm">
            <div className="flex flex-col md:flex-row items-center gap-space-md">
              <div className="relative w-full md:flex-1">
                <Icon name="search" className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant text-[22px]" />
                <input
                  data-testid="wellbeing-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 bg-surface text-on-surface placeholder:text-on-surface-variant font-body-md text-body-md rounded-lg focus:outline-none focus:ring-2 focus:ring-surface-tint transition-all"
                  placeholder="Search every topic across all areas of life..."
                  type="text"
                />
              </div>
              <div className="flex flex-wrap items-center gap-space-xs w-full md:w-auto">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`domain-filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`h-10 px-space-md rounded-full font-label-md text-label-md uppercase tracking-wider transition-all ${filter === f.key ? 'bg-surface-tint text-on-primary' : 'bg-surface text-on-surface-variant hover:bg-surface-container'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-xl max-w-7xl mx-auto">
        <div className="flex items-center justify-between pb-space-lg mb-space-xl">
          <div className="flex flex-wrap items-center gap-space-md">
            <span className="font-headline-sm text-headline-sm uppercase text-outline">Explore Areas of Life</span>
            <span className="px-2 py-0.5 rounded-full bg-primary-container text-surface-tint font-label-md text-label-md">EVERY PART OF DAILY LIFE</span>
          </div>
          <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant hidden sm:inline-block">EVIDENCE-INFORMED • NOT MEDICAL ADVICE</span>
        </div>

        {loading && (
          <div data-testid="domains-loading" className="py-space-2xl flex items-center gap-space-sm text-on-surface-variant">
            <span className="inline-block w-2 h-2 rounded-full bg-surface-tint animate-pulse" />
            <span className="font-label-md text-label-md uppercase tracking-wider">Loading areas of life…</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
          {filtered.map((d) => (
            <article key={d.code} data-testid={`domain-card-${d.code}`} className="group bg-surface-container-low hover:bg-primary-container/40 p-space-lg rounded-lg shadow-sm transition-all duration-300 flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-surface-tint opacity-0 group-hover:opacity-100 transition-opacity" />
              <div>
                <div className="flex items-start justify-between mb-space-lg">
                  <span className="font-headline-lg text-headline-lg font-black text-surface-container-highest group-hover:text-surface-tint transition-colors">{d.n}</span>
                  <span className="font-label-sm text-label-sm uppercase px-2.5 py-1 rounded-full bg-surface text-surface-tint">{d.count} Guides</span>
                </div>
                <p className="font-label-md text-label-md uppercase tracking-widest text-surface-tint mb-space-xs">Area of Life {d.n}</p>
                <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-sm group-hover:text-primary transition-colors">{d.title}</h2>
                <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg leading-relaxed">{d.description}</p>
                <div className="flex flex-wrap gap-1.5 mb-space-lg">
                  {d.tags.map((t) => (<Link key={t.slug} to={`/topics/${t.slug}`} className="font-body-sm text-body-sm px-2 py-1 bg-surface rounded text-on-surface hover:text-surface-tint transition-colors">{t.title}</Link>))}
                </div>
              </div>
              <Link to={`/areas/${d.slug}`} className="pt-space-md flex items-center justify-between" data-testid={`area-link-${d.slug}`}>
                <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant group-hover:text-surface-tint font-bold transition-colors">Explore Topics</span>
                <Icon name="arrow_forward" className="text-outline group-hover:text-surface-tint group-hover:translate-x-1 transition-all" />
              </Link>
            </article>
          ))}

          {filter === 'all' && !query && !loading && (
            <aside className="bg-outline text-surface p-space-lg rounded-lg shadow-md flex flex-col justify-between relative overflow-hidden">
              <div className="absolute -right-8 -bottom-8 w-48 h-48 rounded-full bg-surface-tint/20 blur-2xl pointer-events-none" />
              <div>
                <span className="font-label-sm text-label-sm uppercase tracking-widest text-surface-tint font-bold">CROSS-SYSTEM INTEGRATION</span>
                <h3 className="font-headline-sm text-headline-sm text-surface uppercase mt-2 mb-space-md">The Systems Interlock</h3>
                <p className="font-body-md text-body-md text-surface-container-high leading-relaxed mb-space-lg">No single dimension exists in clinical isolation. Disrupting circadian rhythm suppresses glycemic regulation; strength training activates neurotrophic factors; purpose buffers autonomic stress.</p>
              </div>
              <div className="flex flex-col gap-space-sm">
                <div className="flex items-center gap-2 text-surface-tint font-label-md text-label-md uppercase"><span className="w-1.5 h-1.5 rounded-full bg-surface-tint" />Real-time multi-factorial mapping</div>
                <Link to="/resources" className="w-full h-11 flex items-center justify-center bg-surface text-outline font-label-md text-label-md uppercase tracking-wider rounded hover:bg-surface-tint hover:text-on-primary transition-colors">Browse the Full Library</Link>
              </div>
            </aside>
          )}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="py-space-3xl text-center" data-testid="domains-empty">
            <p className="font-headline-sm text-headline-sm text-outline uppercase">No areas of life found</p>
            <p className="font-body-md text-body-md text-on-surface-variant mt-space-xs">Try a different search term or filter.</p>
          </div>
        )}
      </section>

      {/* DYNAMIC MATRIX */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface-container-low">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center mb-space-xl">
            <div className="lg:col-span-6">
              <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold">BIOMETRIC CROSS-TALK</span>
              <h2 className="font-headline-lg text-headline-lg uppercase text-outline mt-2 mb-space-md">The Dynamic Matrix</h2>
              <p className="font-body-lg text-body-lg text-on-surface-variant leading-relaxed">Understanding how looking after one area of life brings benefits across the others. Each area of life interlocks with the rest.</p>
            </div>
            <div className="lg:col-span-6 flex justify-start lg:justify-end">
              <div className="p-space-md rounded-lg bg-surface shadow-sm inline-flex items-center gap-space-lg">
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-surface-tint" /><span className="font-body-sm text-body-sm text-on-surface font-semibold">Primary Catalyst</span></div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-outline" /><span className="font-body-sm text-body-sm text-on-surface-variant">Downstream Modulation</span></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {MATRIX.map((m) => (
              <div key={m.n} className="bg-surface p-space-lg rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-space-sm mb-space-md">
                  <span className="w-8 h-8 rounded-full bg-primary-container text-surface-tint font-headline-sm text-body-md flex items-center justify-center font-bold">{m.n}</span>
                  <span className="font-headline-sm text-headline-sm text-outline">{m.title}</span>
                </div>
                <p className="font-body-md text-body-md text-on-surface-variant mb-space-md leading-relaxed">{m.desc}</p>
                <div className="w-full bg-surface-container p-3 rounded text-surface-tint font-body-sm text-body-sm font-semibold flex items-center justify-between">
                  <span>{m.affect}</span>
                  <Icon name={m.icon} className="text-[18px]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TOPICS */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-2xl max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-space-xl">
          <div>
            <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold">FROM THE LIBRARY</span>
            <h2 className="font-headline-lg text-headline-lg uppercase text-outline mt-2">Featured Topics</h2>
          </div>
          <Link to="/resources" className="font-title-md text-title-md text-surface-tint hover:underline flex items-center gap-1 mt-4 md:mt-0">
            View the full library <Icon name="arrow_right_alt" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-gutter">
          {featuredGuides.map((g) => (
            <Link to={`/topics/${g.slug}`} key={g.title} className="group bg-surface rounded-lg shadow-sm hover:shadow-md transition-all p-space-md flex flex-col justify-between">
              <div>
                <div className="w-full h-44 rounded bg-surface-container-low mb-space-md overflow-hidden relative">
                  <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={g.title} src={g.img} />
                </div>
                <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider block mb-1">{g.domain}</span>
                <h3 className="font-headline-sm text-body-lg font-bold text-outline group-hover:text-surface-tint transition-colors mb-2">{g.title}</h3>
                <p className="font-body-sm text-body-sm text-on-surface-variant line-clamp-2">A practical guide within {g.domain}.</p>
              </div>
              <div className="pt-4 mt-4 border-t border-surface-container flex items-center justify-between text-surface-tint">
                <span className="font-label-md text-label-md uppercase font-bold">Open Library</span>
                <Icon name="north_east" className="text-[18px]" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-3xl max-w-7xl mx-auto">
        <div className="w-full bg-outline rounded-xl p-space-xl lg:p-space-2xl text-center relative overflow-hidden flex flex-col items-center">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-surface-tint/20 blur-3xl pointer-events-none" />
          <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold mb-space-sm">PERSONAL WELLBEING COMPASS</span>
          <h2 className="font-headline-lg text-headline-lg uppercase text-surface max-w-2xl mb-space-md">Find what matters most to your vitality right now.</h2>
          <p className="font-body-lg text-body-lg text-surface-container-high max-w-xl mb-space-xl leading-relaxed">You do not need to work on everything at once. Pick one area that matters to you right now, and build from there at your own pace.</p>
          <div className="flex flex-col sm:flex-row items-center gap-space-md z-10">
            <Link to="/my-login" className="h-12 px-space-xl rounded bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider shadow-[0_4px_20px_rgba(240,40,223,0.35)] hover:bg-primary transition-all flex items-center">Open My Login</Link>
            <Link to="/resources" className="h-12 px-space-xl rounded bg-surface/10 text-surface font-label-md text-label-md uppercase tracking-wider hover:bg-surface/20 transition-all flex items-center">Dive into the Library</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
